<template>
	<div class="flex flex-col min-h-dvh app">
		<div
			v-if="$nuxt.isOffline"
			class="nuxt-offline d-flex align-items-center"
		>
			<Icon
				:icon="['far', 'exclamation-triangle']"
				class="mr-3 mr-md-2"
			/>
			{{ $t('app.is-offline') }}
		</div>
		<MobileSidebar />
		<main
			class="flex-1 main-wrapper"
			:class="{
				pushed: $store.state.sidebarOpen,
				'pb-5': !isTemplateEditor
			}"
		>
			<AppHeader />
			<SubscriptionBanner v-if="!isTemplateEditor" />
			<client-only>
				<MobileDesktopRecommendationBanner v-if="!isTemplateEditor" />
			</client-only>
			<Container
				class="px-05 px-md-0"
				:class="{ 'py-8': !isTemplateEditor }"
			>
				<nuxt keep-alive :keep-alive-props="{ max: 2 }" />
			</Container>
		</main>

		<NotificationSlideoutComponent
			:open="$store.state.notification.isSlideoutOpen"
			@close="$store.dispatch('notification/toggleSlideout')"
		/>

		<ToastContainerComponent />
		<FooterComponent :reserve-cookie-banner-space="true" />
		<client-only>
			<CookieBanner @open-settings="showCookieSettings = true" />
			<CookieSettingsModal
				v-if="showCookieSettings"
				@close="showCookieSettings = false"
			/>
		</client-only>

		<PortalTarget name="modal-root" multiple />
		<PortalTarget name="tooltip-root" multiple />
	</div>
</template>

<script>
import AppHeader from '~/components/Header/AppHeader'
import { MobileSidebar } from '~/components/Header/_internals'
import ToastContainerComponent from '~/components/toast-container'
import NotificationSlideoutComponent from '~/components/Notifications/NotificationsSlideout'
import FooterComponent from '~/components/footer'
import Container from '~/components/ui/layout/Container'
import Icon from '~/components/ui/common/Icon'
import CookieBanner from '~/components/ui/banners/CookieBanner'
import CookieSettingsModal from '~/components/ui/modals/CookieSettingsModal'
import SubscriptionBanner from '~/components/ui/banners/SubscriptionBanner'
import MobileDesktopRecommendationBanner from '~/components/ui/banners/MobileDesktopRecommendationBanner'
import { createNotificationAwareTitleTemplate } from '~/util/title'

export default {
	components: {
		MobileSidebar,
		AppHeader,
		ToastContainerComponent,
		FooterComponent,
		NotificationSlideoutComponent,
		Container,
		Icon,
		CookieBanner,
		CookieSettingsModal,
		SubscriptionBanner,
		MobileDesktopRecommendationBanner
	},
	data() {
		return {
			showCookieSettings: false
		}
	},
	middleware: ['outdatedBrowser', 'closeSidebarOnSmallDevices'],
	computed: {
		isTemplateEditor() {
			return /^\/organization\/email-templates\/[^/]+\/?$/.test(
				this.$route.path
			)
		},
		unreadCount() {
			return this.$store.state.notification.unreadCount || 0
		}
	},
	head() {
		return {
			titleTemplate: createNotificationAwareTitleTemplate(
				chunk => (chunk ? `${chunk} - apropos.jobs` : 'apropos.jobs'),
				this.unreadCount
			),
			meta: [
				{ hid: 'robots', name: 'robots', content: 'noindex, nofollow' }
			]
		}
	},
	mounted() {
		document.addEventListener('click', this.closeSidebarOnSmallDevices)

		if (
			'Notification' in window &&
			typeof Notification.requestPermission === 'function'
		) {
			if (Notification.permission !== 'denied') {
				setTimeout(() => {
					Notification.requestPermission().then(result => {
						this.$store.commit(
							'notification/setNotificationsPermission',
							result
						)
					})
				}, 8000)
			}
		}
	},
	destroyed() {
		document.removeEventListener('click', this.closeSidebarOnSmallDevices)
	},
	methods: {
		closeSidebarOnSmallDevices() {
			this.$store.dispatch('closeOnSmallDevices')
		}
	},
	watch: {
		'$nuxt.isOffline'(isOffline) {
			if (isOffline) {
				this.$toast(this.$t('app.went-offline'), 'warning', {
					icon: 'wifi-slash'
				})
			} else {
				this.$toast(this.$t('app.went-online'), 'success', {
					icon: 'wifi'
				})
			}
		}
	}
}
</script>
